import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import SeafarerCandidateForm from '../../components/HR/SeafarerCandidateForm';
import CandidateDetailsModal from '../../components/HR/CandidateDetailsModal';
import EmployeeForm, { PendingEmployeeDocument } from '../../components/HR/EmployeeForm';
import Button from '../../components/Form/Button';
import { Candidate, Employee } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCandidates, useCreateCandidate, useUpdateCandidate, useDeleteCandidate } from '../../hooks/useCandidates';
import { useCreateEmployee } from '../../hooks/useEmployees';
import { uploadFiles } from '../../services/upload.service';
import { employeeDocumentsService } from '../../services';
import { bumpCodePrefixSequence } from '../../utils/codePrefixUtils';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, StatsCards, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { getCandidateColumns } from './CandidateColumns';
import { contentManagerService } from '../../services/content-manager.service';
import { reverseTransformFieldNames } from '../../services/admin-resource-helpers';
import { API_CONFIG } from '../../config/api.config';

const Candidates: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | undefined>();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [hiringCandidate, setHiringCandidate] = useState<Candidate | null>(null);

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: candidatesData, isLoading, error, refetch } = useCandidates(queryOptions);
  const candidates = useMemo(() => candidatesData || [], [candidatesData]);

  const createMutation = useCreateCandidate(() => { setShowForm(false); setEditingCandidate(undefined); setTimeout(() => refetch(), 100); });
  const updateMutation = useUpdateCandidate(() => { setShowForm(false); setEditingCandidate(undefined); setTimeout(() => refetch(), 100); });
  const deleteMutation = useDeleteCandidate(() => { setTimeout(() => refetch(), 100); });
  const createEmployeeMutation = useCreateEmployee(() => { setTimeout(() => refetch(), 100); });
  const { hasAction } = useAuth();
  const canHire = hasAction('candidates', 'update');

  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [nationalityFilter, setNationalityFilter] = useState('all');
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const uniquePositions = useMemo(() => {
    if (!candidates || candidates.length === 0) return [];
    return Array.from(new Set(candidates.map(c => c.appliedPosition).filter(Boolean)));
  }, [candidates]);

  const uniqueNationalities = useMemo(() => {
    if (!candidates || candidates.length === 0) return [];
    return Array.from(new Set(candidates.map(c => c.nationality).filter(Boolean)));
  }, [candidates]);

  const sourceFilterOptions = useMemo(() => {
    const defaults = ['website', 'linkedin', 'referral', 'job_board', 'other'];
    const used = (candidates || []).map(c => c.source).filter(Boolean);
    return Array.from(new Set([...defaults, ...used])).map(s => ({
      value: s,
      label: s.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    }));
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    if (!candidates || candidates.length === 0) return [];
    return candidates.filter(candidate => {
      const matchesSearch = !searchTerm || (
        candidate.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.appliedPosition.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.nationality.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (candidate.assignedManagerName && candidate.assignedManagerName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      const matchesPosition = positionFilter === 'all' || candidate.appliedPosition === positionFilter;
      const matchesStatus = statusFilter === 'all' || candidate.status === statusFilter;
      const matchesSource = sourceFilter === 'all' || candidate.source === sourceFilter;
      const matchesNationality = nationalityFilter === 'all' || candidate.nationality === nationalityFilter;
      const matchesExperience = experienceFilter === 'all' ||
        (experienceFilter === '0-2' && candidate.experience <= 2) ||
        (experienceFilter === '3-5' && candidate.experience >= 3 && candidate.experience <= 5) ||
        (experienceFilter === '6-10' && candidate.experience >= 6 && candidate.experience <= 10) ||
        (experienceFilter === '10+' && candidate.experience > 10);
      const matchesRating = ratingFilter === 'all' ||
        (ratingFilter === '0' && candidate.totalRating === 0) ||
        (ratingFilter === '1-3' && candidate.totalRating >= 1 && candidate.totalRating <= 3) ||
        (ratingFilter === '4-6' && candidate.totalRating >= 4 && candidate.totalRating <= 6) ||
        (ratingFilter === '7-8' && candidate.totalRating >= 7 && candidate.totalRating <= 8) ||
        (ratingFilter === '9-10' && candidate.totalRating >= 9 && candidate.totalRating <= 10);
      const appliedDate = new Date(candidate.appliedDate);
      const matchesDateFrom = !dateFromFilter || appliedDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || appliedDate <= new Date(dateToFilter);

      return matchesSearch && matchesPosition && matchesStatus && matchesSource &&
             matchesNationality && matchesExperience && matchesRating && matchesDateFrom && matchesDateTo;
    });
  }, [candidates, searchTerm, positionFilter, statusFilter, sourceFilter, nationalityFilter, experienceFilter, ratingFilter, dateFromFilter, dateToFilter]);

  const buildExportData = () => filteredCandidates.map(candidate => ({
    'Name': `${candidate.firstName} ${candidate.lastName}`, 'Email': candidate.email, 'Phone': candidate.phone,
    'Nationality': candidate.nationality, 'Date of Birth': candidate.dateOfBirth, 'Applied Position': candidate.appliedPosition,
    'Applied Date': candidate.appliedDate, 'Source': candidate.source, 'Experience (Years)': candidate.experience,
    'Education': candidate.education, 'Certificates': (candidate.certificates || []).join(', '), 'Languages': (candidate.languages || []).join(', '),
    'Previous Company': candidate.previousCompany, 'Expected Salary': candidate.expectedSalary, 'Available From': candidate.availableFrom,
    'Status': candidate.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
    'Assigned Manager': candidate.assignedManagerName, 'Manager Feedback': candidate.managerFeedback,
    'Total Rating': candidate.totalRating, 'Assessment Points': candidate.rating?.length || 0, 'Notes': candidate.notes,
    'Created Date': new Date(candidate.createdAt).toLocaleDateString(), 'Updated Date': new Date(candidate.updatedAt).toLocaleDateString(),
  }));

  const handleExport = () => exportToExcel(buildExportData(), `candidates_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Candidate Applications');
  const handleAdd = () => { setEditingCandidate(undefined); setShowForm(true); };
  const handleEdit = (candidate: Candidate) => {
    setEditingCandidate(candidate);
    setShowForm(true);
    // The list payload can omit the big seafarerData json — refetch the full
    // record via the content manager so every submitted field renders.
    const documentId = (candidate as any).documentId;
    if (documentId) {
      contentManagerService
        .getById<any>(API_CONFIG.CONTENT_MANAGER_ENDPOINTS.CANDIDATES, documentId)
        .then(res => {
          const full = res?.data ?? res;
          if (!full || !full.documentId) return;
          setEditingCandidate({ ...candidate, ...reverseTransformFieldNames(full, API_CONFIG.CONTENT_MANAGER_ENDPOINTS.CANDIDATES) });
        })
        .catch(() => { /* keep the list record */ });
    }
  };
  const handleViewDetails = (candidate: Candidate) => { setSelectedCandidate(candidate); setShowDetailsModal(true); };

  const handleDelete = async (candidate: Candidate) => {
    if (!window.confirm(`Are you sure you want to delete the application from "${candidate.firstName} ${candidate.lastName}"?`)) return;
    if (!candidate?.id) return;
    try { await deleteMutation.execute(candidate); } catch (error) { console.error('Delete failed:', error); }
  };

  // The backend candidate lifecycle converts a candidate to an Employee the
  // moment their status becomes "hired" (from any screen — this page, the
  // Hiring applications tab, or a direct API call), so the frontend only
  // needs to surface the outcome.
  const toast = useToast();
  const notifyHired = (candidate: Partial<Candidate>) => {
    const name = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Candidate';
    toast.success(`${name} marked hired — employee record created automatically`);
  };

  // "Hire" opens the full employee form pre-filled from the candidate, so the
  // admin picks the code prefix / rank / branch and the record lands in
  // Employees. hiredEmployeeId is stored on the candidate so the backend
  // lifecycle sees it and does not auto-create a second employee.
  const hirePrefill = (c: Candidate): Employee => {
    const sd = c.seafarerData;
    const passport = sd?.marinePassport || {};
    return {
      name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      arabicName: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      email: c.email || '',
      phoneNumber: c.phone || '',
      nationality: c.nationality || '',
      birthDate: c.dateOfBirth ? c.dateOfBirth.split('T')[0] : '',
      // The seafarer form's "Marine Passport / الجواز البحري" is the seaman's
      // book — carry its number and dates into the employee fields.
      seamanBookNumber: passport.number || '',
      seamanBookIssueDate: passport.issueDate || '',
      seamanBookExpiry: passport.expiryDate || '',
      address: sd?.homeAddress || '',
      position: c.appliedPosition || '',
      rank: c.appliedPosition || '',
      salary: c.expectedSalary || 0,
      hireDate: new Date().toISOString().slice(0, 10),
      status: 'active',
      travelPermit: false,
      payType: 'monthly',
    } as Employee;
  };

  const handleHireSubmit = async (data: Partial<Employee>, pendingDocument?: PendingEmployeeDocument) => {
    try {
      const created: any = await createEmployeeMutation.execute(data);
      await bumpCodePrefixSequence(data.code);
      const employeeDocId = created?.documentId || created?.data?.documentId;
      if (pendingDocument && employeeDocId) {
        await employeeDocumentsService.create({
          employeeId: employeeDocId,
          documentType: pendingDocument.documentType,
          documentNumber: pendingDocument.documentNumber,
          issueDate: pendingDocument.issueDate || null,
          expiryDate: pendingDocument.expiryDate || null,
          status: 'valid',
          filePath: pendingDocument.filePath,
        });
      }
      if (hiringCandidate?.id) {
        await updateMutation.execute({
          id: hiringCandidate.id,
          data: { documentId: hiringCandidate.documentId, status: 'hired', hiredEmployeeId: employeeDocId } as any,
        });
      }
      const name = `${hiringCandidate?.firstName || ''} ${hiringCandidate?.lastName || ''}`.trim() || 'Candidate';
      toast.success(`${name} hired — added to Employees`);
      setHiringCandidate(null);
    } catch (error) {
      console.error('Hire failed:', error);
      toast.error('Hiring failed — please try again');
    }
  };

  const handleFormSubmit = async (candidateData: Partial<Candidate> & { cvFile?: File; pdfFile?: File }) => {
    try {
      const { cvFile, pdfFile, ...rest } = candidateData;
      if (cvFile) {
        const uploaded = await uploadFiles([cvFile]);
        rest.resumeUrl = uploaded[0]?.url;
      }
      if (pdfFile) {
        const uploaded = await uploadFiles([pdfFile]);
        rest.applicationPdfUrl = uploaded[0]?.url;
      }
      if (editingCandidate?.id) {
        const updateData = { ...rest, documentId: editingCandidate.documentId };
        await updateMutation.execute({ id: editingCandidate.id, data: updateData });
        if (rest.status === 'hired' && editingCandidate.status !== 'hired') {
          notifyHired(rest);
        }
      } else {
        await createMutation.execute(rest);
        if (rest.status === 'hired') notifyHired(rest);
      }
    } catch (error) { console.error('Form submission failed:', error); }
  };

  const handleFormCancel = () => { setShowForm(false); setEditingCandidate(undefined); };

  const clearFilters = () => {
    setSearchTerm(''); setPositionFilter('all'); setStatusFilter('all'); setSourceFilter('all');
    setNationalityFilter('all'); setExperienceFilter('all'); setRatingFilter('all');
    setDateFromFilter(''); setDateToFilter('');
  };

  const newCandidates = filteredCandidates.filter(c => c.status === 'new');
  const underReview = filteredCandidates.filter(c => c.status === 'under_review');
  const interviewed = filteredCandidates.filter(c => c.status === 'interviewed' || c.status === 'interview_scheduled');
  const hired = filteredCandidates.filter(c => c.status === 'hired');

  const columns = getCandidateColumns(handleViewDetails);

  const filters: FilterConfig[] = [
    { key: 'position', label: 'Position', type: 'select', value: positionFilter, onChange: setPositionFilter,
      options: [{ value: 'all', label: 'All Positions' }, ...uniquePositions.map(p => ({ value: p, label: p }))] },
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'new', label: 'New' }, { value: 'under_review', label: 'Under Review' },
        { value: 'interview_scheduled', label: 'Interview Scheduled' }, { value: 'interviewed', label: 'Interviewed' },
        { value: 'hired', label: 'Hired' }, { value: 'rejected', label: 'Rejected' }] },
    { key: 'source', label: 'Source', type: 'select', value: sourceFilter, onChange: setSourceFilter,
      options: [{ value: 'all', label: 'All Sources' }, ...sourceFilterOptions] },
    { key: 'nationality', label: 'Nationality', type: 'select', value: nationalityFilter, onChange: setNationalityFilter,
      options: [{ value: 'all', label: 'All Nationalities' }, ...uniqueNationalities.map(n => ({ value: n, label: n }))] },
    { key: 'experience', label: 'Experience', type: 'select', value: experienceFilter, onChange: setExperienceFilter,
      options: [{ value: 'all', label: 'All' }, { value: '0-2', label: '0-2 years' }, { value: '3-5', label: '3-5 years' },
        { value: '6-10', label: '6-10 years' }, { value: '10+', label: '10+ years' }] },
    { key: 'rating', label: 'Rating', type: 'select', value: ratingFilter, onChange: setRatingFilter,
      options: [{ value: 'all', label: 'All Ratings' }, { value: '0', label: 'Not Rated' }, { value: '1-3', label: '1-3 Points' },
        { value: '4-6', label: '4-6 Points' }, { value: '7-8', label: '7-8 Points' }, { value: '9-10', label: '9-10 Points' }] },
    { key: 'dateFrom', label: 'Applied From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Applied To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const stats = [
    { label: 'New Applications', value: newCandidates.length, color: 'gray' as const },
    { label: 'Under Review', value: underReview.length, color: 'yellow' as const },
    { label: 'Interviewed', value: interviewed.length, color: 'blue' as const },
    { label: 'Hired', value: hired.length, color: 'green' as const },
  ];

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading candidates. Please try again." onRetry={() => refetch()} entityName="candidates" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Candidate Applications" description="Manage job applications and candidate evaluations from website and other sources." />
      <StatsCards stats={stats} />

      {!showForm && (
        <>
          <FilterBar
            searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search candidates..."
            filters={filters} onClearFilters={clearFilters}
            totalCount={candidates.length} filteredCount={filteredCandidates.length} entityName="Candidate Applications"
            onAdd={handleAdd} addLabel="Add Candidate" onExport={handleExport} onPrint={handlePrint}
          />
          <Table
            data={filteredCandidates} columns={columns}
            onEdit={handleEdit} onDelete={handleDelete} showAddButton={false} searchable={false}
            renderActions={canHire ? (c: Candidate) => c.status !== 'hired' && (
              <Button variant="primary" size="sm" onClick={() => setHiringCandidate(c)}>Hire</Button>
            ) : undefined}
          />
        </>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            {editingCandidate ? 'Edit Candidate' : 'Add Candidate'} — Seafarer Application Form / نموذج التقديم البحري
          </h3>
          <SeafarerCandidateForm candidate={editingCandidate} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
        </div>
      )}

      {hiringCandidate && (
        <EmployeeForm
          employee={hirePrefill(hiringCandidate)}
          title={`Hire ${hiringCandidate.firstName} ${hiringCandidate.lastName}`}
          submitLabel="Hire & Add to Employees"
          onSubmit={handleHireSubmit}
          onCancel={() => setHiringCandidate(null)}
        />
      )}

      {showDetailsModal && selectedCandidate && (
        <CandidateDetailsModal
          candidate={selectedCandidate}
          onClose={() => setShowDetailsModal(false)}
          onUpdate={async (updatedCandidate) => {
            try {
              await updateMutation.execute({ id: updatedCandidate.id, data: updatedCandidate });
              setShowDetailsModal(false);
              if (updatedCandidate.status === 'hired' && selectedCandidate.status !== 'hired') {
                notifyHired(updatedCandidate);
              }
            } catch (error) { console.error('Update failed:', error); }
          }}
        />
      )}
    </div>
  );
};

export default Candidates;
